import React, { useMemo, useState } from "react";
import { Trophy, Calendar, Plus, X, Award, RefreshCw, Trash2, DollarSign, Copy, Printer } from "lucide-react";
import { WinnersPrintView } from "./WinnersPrintView";

const todayISO = () => new Date().toISOString().slice(0, 10);

const statusFor = (d) => {
  if (d.winning_ticket != null) return "complete";
  const today = todayISO();
  if (d.drawing_date === today) return "today";
  if (d.drawing_date < today) return "overdue";
  return "upcoming";
};

const statusMeta = {
  complete: { label: "Complete", bg: "var(--surface-subtle)", color: "var(--brand-primary-hover)" },
  today: { label: "Today", bg: "var(--semantic-warning-soft)", color: "var(--semantic-warning)" },
  overdue: { label: "Overdue", bg: "var(--semantic-danger-soft)", color: "var(--semantic-danger)" },
  upcoming: { label: "Upcoming", bg: "var(--semantic-success-soft)", color: "var(--semantic-success)" },
};

const typeLabel = (t) => (t === "main" ? "Main Drawing" : "Early Bird");

export const DrawingsTab = ({
  drawings,
  tickets,
  year,
  isReadOnlyYear,
  onCreate,
  onUpdate,
  onDelete,
  onDraw,
  onClear,
  onSetPrizePaid,
  fetchEligible,
  showToast,
}) => {
  const [showCreate, setShowCreate] = useState(false);
  const [drawFor, setDrawFor] = useState(null); // drawing being conducted
  const [confirmDelete, setConfirmDelete] = useState(null); // drawing to delete
  const [editing, setEditing] = useState(null); // drawing being edited
  const [duplicating, setDuplicating] = useState(null); // source drawing being duplicated

  const sortedDrawings = useMemo(
    () => [...(drawings || [])].sort((a, b) => a.drawing_date.localeCompare(b.drawing_date)),
    [drawings]
  );

  const upcoming = sortedDrawings.filter((d) => d.winning_ticket == null);
  const complete = sortedDrawings.filter((d) => d.winning_ticket != null);

  const fundsReceivedCount = useMemo(() => {
    let c = 0;
    for (let i = 1; i <= 400; i++) if (tickets[i]?.status === "funds_received") c++;
    return c;
  }, [tickets]);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-5" data-testid="drawings-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: "var(--ink-primary)" }}>
            <Trophy className="inline h-5 w-5 mr-1.5 -mt-0.5" style={{ color: "var(--semantic-warning)" }} />
            Drawings {isReadOnlyYear && <span className="text-sm ml-2" style={{ color: "var(--ink-tertiary)" }}>· {year} (read-only)</span>}
          </h1>
          <p className="text-sm mt-0.5" style={{ color: "var(--ink-tertiary)" }}>
            Schedule drawing dates, record winners. Only <strong>funds-received</strong> tickets paid on or before each drawing date are eligible.
            Currently <strong>{fundsReceivedCount}</strong> tickets are paid in full.
          </p>
        </div>
        {!isReadOnlyYear && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            data-testid="drawing-add-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold text-white transition-colors hover:opacity-90"
            style={{ background: "var(--brand-primary)" }}
          >
            <Plus className="h-4 w-4" /> Add drawing
          </button>
        )}
      </div>

      {sortedDrawings.length === 0 && (
        <div className="bg-[color:var(--surface-card)] border rounded-lg p-8 text-center" style={{ borderColor: "var(--border-default)" }} data-testid="drawings-empty">
          <Calendar className="h-10 w-10 mx-auto mb-2" style={{ color: "var(--ink-tertiary)" }} />
          <p className="text-sm" style={{ color: "var(--ink-tertiary)" }}>
            {isReadOnlyYear ? "No drawings on record for this year." : "No drawings scheduled yet. Click \u201cAdd drawing\u201d to create the first one."}
          </p>
        </div>
      )}

      {upcoming.length > 0 && (
        <div className="space-y-3" data-testid="drawings-upcoming">
          <h2 className="text-sm font-semibold uppercase tracking-wide" style={{ color: "var(--ink-tertiary)", letterSpacing: "0.04em" }}>Scheduled</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {upcoming.map((d) => (
              <DrawingCard
                key={d.id}
                d={d}
                readOnly={isReadOnlyYear}
                onDraw={() => setDrawFor(d)}
                onEdit={() => setEditing(d)}
                onDuplicate={() => setDuplicating(d)}
                onDelete={() => setConfirmDelete(d)}
              />
            ))}
          </div>
        </div>
      )}

      {complete.length > 0 && (
        <div className="space-y-3" data-testid="drawings-complete">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide" style={{ color: "var(--ink-tertiary)", letterSpacing: "0.04em" }}>Winners</h2>
            <button
              type="button"
              onClick={() => { setTimeout(() => window.print(), 50); }}
              data-testid="drawings-print-winners"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors hover:border-[color:var(--brand-primary)] hover:text-[color:var(--brand-primary)]"
              style={{ borderColor: "var(--border-default)", color: "var(--ink-secondary)", background: "var(--surface-card)" }}
              title="Print or save the winners list as a PDF"
            >
              <Printer className="h-3.5 w-3.5" />
              Print winners (PDF)
            </button>
          </div>
          <div className="bg-[color:var(--surface-card)] border rounded-lg overflow-hidden" style={{ borderColor: "var(--border-default)" }}>
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--surface-subtle)]">
                <tr style={{ color: "var(--ink-tertiary)" }}>
                  <th className="text-left px-3 py-2 font-semibold">Drawing</th>
                  <th className="text-left px-3 py-2 font-semibold">Date</th>
                  <th className="text-left px-3 py-2 font-semibold">Ticket #</th>
                  <th className="text-left px-3 py-2 font-semibold">Buyer</th>
                  <th className="text-left px-3 py-2 font-semibold">Phone</th>
                  <th className="text-left px-3 py-2 font-semibold">Prize</th>
                  <th className="text-left px-3 py-2 font-semibold">Paid</th>
                  <th className="text-left px-3 py-2 font-semibold">Pool</th>
                  {!isReadOnlyYear && <th className="text-right px-3 py-2 font-semibold">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {complete.map((d) => (
                  <tr key={d.id} className="border-t" style={{ borderColor: "var(--border-default)" }} data-testid={`drawing-winner-${d.id}`}>
                    <td className="px-3 py-2 font-medium">{d.name}</td>
                    <td className="px-3 py-2">{d.drawing_date}</td>
                    <td className="px-3 py-2 font-semibold" style={{ color: "var(--brand-primary)" }}>#{d.winning_ticket}</td>
                    <td className="px-3 py-2">{d.winning_buyer || "\u2014"}</td>
                    <td className="px-3 py-2 font-mono text-xs" data-testid={`drawing-winner-phone-${d.id}`}>
                      {d.winning_phone ? (
                        <a
                          href={`tel:${d.winning_phone.replace(/[^+\d]/g, "")}`}
                          className="hover:underline"
                          style={{ color: "var(--brand-primary)" }}
                          title="Tap to call"
                        >
                          {d.winning_phone}
                        </a>
                      ) : (
                        <span style={{ color: "var(--ink-tertiary)" }}>{"\u2014"}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">${Number(d.prize_amount).toFixed(2)}</td>
                    <td className="px-3 py-2" data-testid={`drawing-paid-cell-${d.id}`}>
                      {isReadOnlyYear ? (
                        // Historical years: show a static glyph so the column
                        // still reads at a glance but nobody can accidentally toggle it.
                        <span
                          className="inline-flex items-center gap-1.5 text-xs font-semibold"
                          style={{ color: d.prize_paid ? "var(--semantic-success)" : "var(--ink-tertiary)" }}
                          title={d.prize_paid ? `Paid ${d.prize_paid_at ? "on " + new Date(d.prize_paid_at).toLocaleDateString() : ""}` : "Not paid"}
                        >
                          <span aria-hidden="true">{d.prize_paid ? "\u2611" : "\u2610"}</span>
                          {d.prize_paid ? "Paid" : "Not paid"}
                        </span>
                      ) : (
                        <label
                          className="inline-flex items-center gap-1.5 cursor-pointer select-none text-xs font-semibold"
                          style={{ color: d.prize_paid ? "var(--semantic-success)" : "var(--ink-secondary)" }}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 cursor-pointer"
                            checked={!!d.prize_paid}
                            onChange={async (e) => {
                              const paid = e.target.checked;
                              const res = await onSetPrizePaid?.(d.id, paid);
                              if (res?.ok) {
                                showToast?.(paid
                                  ? `\u201c${d.name}\u201d prize marked paid`
                                  : `\u201c${d.name}\u201d prize unmarked`);
                              } else {
                                showToast?.(res?.error || "Could not update prize status");
                              }
                            }}
                            data-testid={`drawing-paid-toggle-${d.id}`}
                            aria-label={`Prize paid for ${d.name}`}
                          />
                          {d.prize_paid ? (
                            <span title={d.prize_paid_at ? `Paid ${new Date(d.prize_paid_at).toLocaleDateString()} by ${d.prize_paid_by_name || "—"}` : "Paid"}>
                              Paid
                            </span>
                          ) : (
                            <span>Not paid</span>
                          )}
                        </label>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs" style={{ color: "var(--ink-tertiary)" }}>{d.eligible_count} eligible ({d.draw_mode})</td>
                    {!isReadOnlyYear && (
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={async () => {
                            const res = await onClear(d.id);
                            if (res.ok) showToast?.(`Winner cleared for \u201c${d.name}\u201d`);
                            else showToast?.(res.error || "Could not clear winner");
                          }}
                          data-testid={`drawing-clear-${d.id}`}
                          className="text-xs font-medium px-2 py-1 rounded-md border bg-[color:var(--surface-card)] hover:bg-[color:var(--surface-subtle)]"
                          style={{ borderColor: "var(--border-default)", color: "var(--ink-tertiary)" }}
                        >
                          <RefreshCw className="inline h-3 w-3 mr-1" /> Redraw
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreate && (
        <DrawingFormModal
          title="Add drawing"
          onSubmit={async (payload) => {
            const res = await onCreate(payload);
            if (res.ok) {
              showToast?.(`\u201c${payload.name}\u201d added`);
              setShowCreate(false);
            }
            return res;
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {editing && (
        <DrawingFormModal
          title={`Edit \u201c${editing.name}\u201d`}
          initial={editing}
          onSubmit={async (payload) => {
            const res = await onUpdate(editing.id, payload);
            if (res.ok) {
              showToast?.("Drawing updated");
              setEditing(null);
            }
            return res;
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      {duplicating && (
        <DrawingFormModal
          title={`Duplicate \u201c${duplicating.name}\u201d`}
          initial={{ ...duplicating, name: `${duplicating.name} (copy)` }}
          onSubmit={async (payload) => {
            const res = await onCreate(payload);
            if (res.ok) {
              showToast?.(`\u201c${payload.name}\u201d added`);
              setDuplicating(null);
            }
            return res;
          }}
          onCancel={() => setDuplicating(null)}
        />
      )}

      {drawFor && (
        <ConductDrawModal
          drawing={drawFor}
          fetchEligible={fetchEligible}
          onSubmit={async (payload) => {
            const res = await onDraw(drawFor.id, payload);
            if (res.ok) {
              const w = res.drawing;
              showToast?.(`\ud83c\udfc6 Ticket #${w.winning_ticket} (${w.winning_buyer}) wins ${drawFor.name}`);
              setDrawFor(null);
            }
            return res;
          }}
          onCancel={() => setDrawFor(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title={`Delete \u201c${confirmDelete.name}\u201d?`}
          body="This scheduled drawing will be permanently removed. This cannot be undone."
          confirmLabel="Delete drawing"
          destructive
          onConfirm={async () => {
            const res = await onDelete(confirmDelete.id);
            if (res.ok) showToast?.("Drawing deleted");
            else showToast?.(res.error || "Could not delete");
            setConfirmDelete(null);
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Off-screen letterhead used only when the user clicks Print winners. */}
      <WinnersPrintView year={year} drawings={complete} />
    </section>
  );
};

// ---------- Sub-components ----------

const DrawingCard = ({ d, readOnly, onDraw, onEdit, onDuplicate, onDelete }) => {
  const s = statusFor(d);
  const meta = statusMeta[s];
  return (
    <div
      className="bg-[color:var(--surface-card)] border rounded-lg p-4 shadow-sm"
      style={{ borderColor: "var(--border-default)" }}
      data-testid={`drawing-card-${d.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide mb-0.5" style={{ color: "var(--ink-tertiary)", letterSpacing: "0.04em" }}>{typeLabel(d.drawing_type)}</p>
          <h3 className="text-base font-semibold truncate" style={{ color: "var(--ink-primary)" }}>{d.name}</h3>
        </div>
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: meta.bg, color: meta.color }}>
          {meta.label}
        </span>
      </div>
      <dl className="space-y-1 text-sm mb-3">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" style={{ color: "var(--ink-tertiary)" }} />
          <dt className="text-xs" style={{ color: "var(--ink-tertiary)" }}>Drawing date:</dt>
          <dd className="font-medium">{d.drawing_date}</dd>
        </div>
        <div className="flex items-center gap-1.5">
          <DollarSign className="h-3.5 w-3.5" style={{ color: "var(--ink-tertiary)" }} />
          <dt className="text-xs" style={{ color: "var(--ink-tertiary)" }}>Prize:</dt>
          <dd className="font-medium">${Number(d.prize_amount).toFixed(2)}</dd>
        </div>
        {d.notes && (
          <p className="text-xs pt-1" style={{ color: "var(--ink-tertiary)" }}>{d.notes}</p>
        )}
      </dl>
      {!readOnly && (
        <div className="flex flex-wrap gap-2 pt-2 border-t" style={{ borderColor: "var(--border-default)" }}>
          <button
            type="button"
            onClick={onDraw}
            data-testid={`drawing-draw-${d.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold text-white transition-colors hover:opacity-90"
            style={{ background: "var(--brand-primary)" }}
          >
            <Award className="h-4 w-4" /> Conduct drawing
          </button>
          <button
            type="button"
            onClick={onEdit}
            data-testid={`drawing-edit-${d.id}`}
            className="px-2 py-1.5 rounded-md text-xs font-semibold border bg-[color:var(--surface-card)] hover:bg-[color:var(--surface-subtle)]"
            style={{ borderColor: "var(--border-default)", color: "var(--ink-primary)" }}
          >
            Edit
          </button>
          <button
            type="button"
            onClick={onDuplicate}
            data-testid={`drawing-duplicate-${d.id}`}
            className="px-2 py-1.5 rounded-md text-xs font-semibold border bg-[color:var(--surface-card)] hover:bg-[color:var(--surface-subtle)] inline-flex items-center gap-1"
            style={{ borderColor: "var(--border-default)", color: "var(--ink-primary)" }}
            title="Duplicate this drawing"
            aria-label={`Duplicate ${d.name}`}
          >
            <Copy className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Duplicate</span>
          </button>
          <button
            type="button"
            onClick={onDelete}
            data-testid={`drawing-delete-${d.id}`}
            className="px-2 py-1.5 rounded-md text-xs font-semibold border bg-[color:var(--surface-card)] hover:bg-rose-50"
            style={{ borderColor: "var(--border-default)", color: "var(--semantic-danger)" }}
            aria-label={`Delete ${d.name}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

const DrawingFormModal = ({ title, initial, onSubmit, onCancel }) => {
  const [name, setName] = useState(initial?.name || "");
  const [drawingDate, setDrawingDate] = useState(initial?.drawing_date || todayISO());
  const [drawingType, setDrawingType] = useState(initial?.drawing_type || "early_bird");
  const [prize, setPrize] = useState(String(initial?.prize_amount ?? 100));
  const [notes, setNotes] = useState(initial?.notes || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (!name.trim()) { setErr("Drawing name is required."); return; }
    if (!drawingDate) { setErr("Drawing date is required."); return; }
    const p = parseFloat(prize);
    if (Number.isNaN(p) || p < 0) { setErr("Prize amount must be 0 or higher."); return; }
    setBusy(true);
    const res = await onSubmit({
      name: name.trim(),
      drawing_date: drawingDate,
      drawing_type: drawingType,
      prize_amount: p,
      notes: notes.trim(),
    });
    setBusy(false);
    if (!res.ok) setErr(res.error || "Could not save.");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog" aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
      style={{ background: "rgba(32,31,30,0.55)" }}
      data-testid="drawing-form-modal"
    >
      <div className="bg-[color:var(--surface-card)] rounded-lg w-full max-w-md border shadow-lg" style={{ borderColor: "var(--border-default)" }}>
        <div className="px-6 pt-5 pb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold" style={{ color: "var(--ink-primary)" }}>{title}</h3>
          <button type="button" onClick={onCancel} className="p-1 rounded hover:bg-[color:var(--surface-subtle)]" aria-label="Close">
            <X className="h-4 w-4" style={{ color: "var(--ink-tertiary)" }} />
          </button>
        </div>
        <div className="px-6 pb-4 space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: "var(--ink-tertiary)" }}>Drawing name</label>
            <input
              data-testid="drawing-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. First Early Bird"
              className="w-full rounded-md border bg-[color:var(--surface-card)] px-3 py-2 text-base focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
              style={{ borderColor: "var(--border-default)" }}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: "var(--ink-tertiary)" }}>Date</label>
              <input
                data-testid="drawing-date"
                type="date"
                value={drawingDate}
                onChange={(e) => setDrawingDate(e.target.value)}
                className="w-full rounded-md border bg-[color:var(--surface-card)] px-3 py-2 text-base focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
                style={{ borderColor: "var(--border-default)" }}
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: "var(--ink-tertiary)" }}>Type</label>
              <select
                data-testid="drawing-type"
                value={drawingType}
                onChange={(e) => setDrawingType(e.target.value)}
                className="w-full rounded-md border bg-[color:var(--surface-card)] px-3 py-2 text-base focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
                style={{ borderColor: "var(--border-default)" }}
              >
                <option value="early_bird">Early Bird</option>
                <option value="main">Main Drawing</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: "var(--ink-tertiary)" }}>Prize amount ($)</label>
            <input
              data-testid="drawing-prize"
              type="number"
              step="0.01"
              min="0"
              value={prize}
              onChange={(e) => setPrize(e.target.value)}
              className="w-full rounded-md border bg-[color:var(--surface-card)] px-3 py-2 text-base focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
              style={{ borderColor: "var(--border-default)" }}
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: "var(--ink-tertiary)" }}>Notes (optional)</label>
            <input
              data-testid="drawing-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional details"
              className="w-full rounded-md border bg-[color:var(--surface-card)] px-3 py-2 text-base focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
              style={{ borderColor: "var(--border-default)" }}
            />
          </div>
        </div>
        {err && <p className="px-6 pb-2 text-sm font-medium" style={{ color: "var(--semantic-danger)" }} data-testid="drawing-form-error">{err}</p>}
        <div className="px-6 py-4 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--border-default)" }}>
          <button type="button" onClick={onCancel} disabled={busy} data-testid="drawing-form-cancel"
            className="px-4 py-2 rounded-md border bg-[color:var(--surface-card)] text-sm font-medium hover:border-[color:var(--ink-tertiary)] disabled:opacity-50"
            style={{ borderColor: "var(--border-default)", color: "var(--ink-primary)" }}>Cancel</button>
          <button type="button" onClick={submit} disabled={busy} data-testid="drawing-form-submit"
            className="px-4 py-2 rounded-md text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
            style={{ background: "var(--brand-primary)" }}>
            {busy ? "Saving\u2026" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ConductDrawModal = ({ drawing, fetchEligible, onSubmit, onCancel }) => {
  const [mode, setMode] = useState("manual"); // physical drawing is primary per user pref
  const [ticketNumber, setTicketNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [pool, setPool] = useState({ count: 0, tickets: [], loaded: false });

  React.useEffect(() => {
    let active = true;
    fetchEligible(drawing.id).then((res) => {
      if (!active) return;
      if (res.ok) setPool({ count: res.count, tickets: res.tickets, loaded: true });
      else setPool({ count: 0, tickets: [], loaded: true });
    });
    return () => { active = false; };
  }, [drawing.id, fetchEligible]);

  const submit = async () => {
    setErr("");
    if (mode === "manual") {
      const n = parseInt(ticketNumber, 10);
      if (Number.isNaN(n) || n < 1) { setErr("Enter a valid ticket number."); return; }
      if (!pool.tickets.includes(n)) { setErr(`Ticket #${n} is not eligible for this drawing.`); return; }
      setBusy(true);
      const res = await onSubmit({ mode: "manual", ticket_number: n, notes });
      setBusy(false);
      if (!res.ok) setErr(res.error || "Could not record winner.");
    } else {
      if (!window.confirm(`Let the app randomly pick a winner from the ${pool.count} eligible tickets?`)) return;
      setBusy(true);
      const res = await onSubmit({ mode: "random", notes });
      setBusy(false);
      if (!res.ok) setErr(res.error || "Could not perform draw.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog" aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
      style={{ background: "rgba(32,31,30,0.55)" }}
      data-testid="drawing-conduct-modal"
    >
      <div className="bg-[color:var(--surface-card)] rounded-lg w-full max-w-md border shadow-lg" style={{ borderColor: "var(--border-default)" }}>
        <div className="px-6 pt-5 pb-3 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold" style={{ color: "var(--ink-primary)" }}>
              <Trophy className="inline h-4 w-4 mr-1 -mt-0.5" style={{ color: "var(--semantic-warning)" }} />
              Conduct: {drawing.name}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: "var(--ink-tertiary)" }}>
              {drawing.drawing_date} · ${Number(drawing.prize_amount).toFixed(2)} prize
            </p>
          </div>
          <button type="button" onClick={onCancel} className="p-1 rounded hover:bg-[color:var(--surface-subtle)]" aria-label="Close">
            <X className="h-4 w-4" style={{ color: "var(--ink-tertiary)" }} />
          </button>
        </div>
        <div className="px-6 pb-4 space-y-4">
          <div className="rounded-md border p-3" style={{ borderColor: "var(--surface-subtle)", background: "var(--surface-subtle)" }} data-testid="drawing-eligible-panel">
            <p className="text-sm">
              <strong>{pool.loaded ? pool.count : "\u2026"}</strong> eligible tickets (funds-received on or before {drawing.drawing_date}).
            </p>
            {pool.loaded && pool.count === 0 && (
              <p className="text-xs mt-1" style={{ color: "var(--semantic-danger)" }}>No tickets are eligible — mark tickets as funds received first.</p>
            )}
          </div>

          <div className="flex gap-2">
            <button type="button" onClick={() => setMode("manual")}
              data-testid="drawing-mode-manual"
              aria-pressed={mode === "manual"}
              className={`flex-1 px-3 py-2.5 rounded-md text-sm font-semibold border transition-colors ${
                mode === "manual"
                  ? "bg-[color:var(--brand-primary)]/10 border-[color:var(--brand-primary)] text-[color:var(--brand-primary)] ring-1 ring-[color:var(--brand-primary)]"
                  : "bg-[color:var(--surface-card)] border-[color:var(--border-default)] text-[color:var(--ink-secondary)] hover:border-[color:var(--brand-primary)]"
              }`}>
              Enter winning ticket
            </button>
            <button type="button" onClick={() => setMode("random")}
              data-testid="drawing-mode-random"
              aria-pressed={mode === "random"}
              className={`flex-1 px-3 py-2.5 rounded-md text-sm font-semibold border transition-colors ${
                mode === "random"
                  ? "bg-emerald-50 border-emerald-500 text-emerald-700 ring-1 ring-emerald-500"
                  : "bg-[color:var(--surface-card)] border-[color:var(--border-default)] text-[color:var(--ink-secondary)] hover:border-emerald-400"
              }`}>
              Random pick
            </button>
          </div>

          {mode === "manual" && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: "var(--ink-tertiary)" }}>Winning ticket #</label>
              <input
                data-testid="drawing-manual-ticket"
                type="number"
                min="1"
                max="400"
                value={ticketNumber}
                onChange={(e) => setTicketNumber(e.target.value)}
                placeholder="Enter the number drawn"
                className="w-full rounded-md border bg-[color:var(--surface-card)] px-3 py-2 text-base focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
                style={{ borderColor: "var(--border-default)" }}
              />
              <p className="text-xs mt-1" style={{ color: "var(--ink-tertiary)" }}>Only funds-received tickets paid on or before {drawing.drawing_date} are accepted.</p>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: "var(--ink-tertiary)" }}>Notes (optional)</label>
            <input
              data-testid="drawing-conduct-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. drawn at lodge meeting"
              className="w-full rounded-md border bg-[color:var(--surface-card)] px-3 py-2 text-base focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
              style={{ borderColor: "var(--border-default)" }}
            />
          </div>
        </div>
        {err && <p className="px-6 pb-2 text-sm font-medium" style={{ color: "var(--semantic-danger)" }} data-testid="drawing-conduct-error">{err}</p>}
        <div className="px-6 py-4 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--border-default)" }}>
          <button type="button" onClick={onCancel} disabled={busy} data-testid="drawing-conduct-cancel"
            className="px-4 py-2 rounded-md border bg-[color:var(--surface-card)] text-sm font-medium hover:border-[color:var(--ink-tertiary)] disabled:opacity-50"
            style={{ borderColor: "var(--border-default)", color: "var(--ink-primary)" }}>Cancel</button>
          <button type="button" onClick={submit} disabled={busy || !pool.loaded || pool.count === 0} data-testid="drawing-conduct-submit"
            className="px-4 py-2 rounded-md text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
            style={{ background: "var(--brand-primary)" }}>
            {busy ? "Recording\u2026" : "Record winner"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmModal = ({ title, body, confirmLabel, destructive, onConfirm, onCancel }) => {
  const [busy, setBusy] = useState(false);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog" aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
      style={{ background: "rgba(32,31,30,0.55)" }}
      data-testid="drawing-confirm-modal"
    >
      <div className="bg-[color:var(--surface-card)] rounded-lg w-full max-w-md border shadow-lg" style={{ borderColor: "var(--border-default)" }}>
        <div className="px-6 pt-5 pb-3">
          <h3 className="text-base font-semibold" style={{ color: destructive ? "var(--semantic-danger)" : "var(--ink-primary)" }}>{title}</h3>
        </div>
        <div className="px-6 pb-4 text-sm" style={{ color: "var(--ink-tertiary)" }}>{body}</div>
        <div className="px-6 py-4 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--border-default)" }}>
          <button type="button" onClick={onCancel} disabled={busy}
            className="px-4 py-2 rounded-md border bg-[color:var(--surface-card)] text-sm font-medium hover:border-[color:var(--ink-tertiary)] disabled:opacity-50"
            style={{ borderColor: "var(--border-default)", color: "var(--ink-primary)" }}>Cancel</button>
          <button type="button"
            onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); }}
            disabled={busy}
            data-testid="drawing-confirm-yes"
            className="px-4 py-2 rounded-md text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
            style={{ background: destructive ? "var(--semantic-danger)" : "var(--brand-primary)" }}>
            {busy ? "\u2026" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
